import ApiClient from 'http/ApiClient'
import createReference from 'bibliography/application/createReference'
import { ReferenceDto } from 'bibliography/domain/referenceDto'
import {
  RealiaEntry,
  ReallexikonEntry,
  AfoRegisterEntry,
  RealiaCrossReference,
} from 'realia/domain/RealiaEntry'

interface ReallexikonEntryDto {
  readonly id: string
  readonly title: string
  readonly reference: ReferenceDto | null
}

interface RealiaCrossReferenceDto {
  readonly id: string
  readonly lemma: string
}

interface AfoRegisterEntryDto {
  readonly mainWord: string
  readonly note: string
  readonly afoVolume?: string
  readonly page?: string
  readonly AfO: string
  readonly reference: string
  readonly crossReference: string
  readonly crossReferences: Nullable<RealiaCrossReferenceDto>
}

type Nullable<T> = readonly T[] | T | null | undefined

interface RealiaEntryDto {
  readonly _id: string
  readonly realiaId?: string
  readonly relatedTerms: Nullable<string>
  readonly type: Nullable<string>
  readonly wikidataId: Nullable<string>
  readonly afoRegister: Nullable<AfoRegisterEntryDto>
  readonly reallexikon: Nullable<ReallexikonEntryDto>
  readonly crossReferences: Nullable<RealiaCrossReferenceDto>
  readonly afoCrossReferences: Nullable<RealiaCrossReferenceDto>
  readonly references: Nullable<ReferenceDto>
}

function toArray<T>(value: Nullable<T>): readonly T[] {
  if (value === null || value === undefined) {
    return []
  }
  return Array.isArray(value) ? value : [value as T]
}

function mapReallexikonEntry(dto: ReallexikonEntryDto): ReallexikonEntry {
  return {
    id: dto.id,
    title: dto.title,
    reference: dto.reference ? createReference(dto.reference) : null,
  }
}

function mapCrossReference(dto: RealiaCrossReferenceDto): RealiaCrossReference {
  return { id: dto.id, lemma: dto.lemma }
}

function parseAfoCitation(afo: string): {
  afoVolume: string
  year: string
  page: string
} {
  const trimmed = afo.trim()
  const withYear = trimmed.match(/^(.*?)\s*\(([^)]*)\)\s*,?\s*(.*)$/)
  if (withYear) {
    return {
      afoVolume: withYear[1].trim(),
      year: withYear[2].trim(),
      page: withYear[3].trim(),
    }
  }
  const lastComma = trimmed.lastIndexOf(',')
  if (lastComma !== -1) {
    return {
      afoVolume: trimmed.slice(0, lastComma).trim(),
      year: '',
      page: trimmed.slice(lastComma + 1).trim(),
    }
  }
  return { afoVolume: trimmed, year: '', page: '' }
}

function mapAfoRegisterEntry(dto: AfoRegisterEntryDto): AfoRegisterEntry {
  const parsed = parseAfoCitation(dto.AfO)
  return {
    mainWord: dto.mainWord,
    note: dto.note,
    afoVolume: dto.afoVolume ?? parsed.afoVolume,
    year: parsed.year,
    page: dto.page ?? parsed.page,
    AfO: dto.AfO,
    reference: dto.reference,
    crossReference: dto.crossReference,
    crossReferences: toArray(dto.crossReferences).map(mapCrossReference),
  }
}

function mapRealiaEntry(dto: RealiaEntryDto): RealiaEntry {
  const reallexikonDtos = toArray(dto.reallexikon)
  const reallexikonReferenceIds = new Set(
    reallexikonDtos
      .map((entry) => entry.reference?.id)
      .filter((id): id is string => id != null),
  )
  return {
    id: dto._id,
    realiaId: dto.realiaId ?? dto._id,
    relatedTerms: toArray(dto.relatedTerms),
    type: toArray(dto.type),
    wikidataId: toArray(dto.wikidataId),
    afoRegister: toArray(dto.afoRegister).map(mapAfoRegisterEntry),
    reallexikon: reallexikonDtos.map(mapReallexikonEntry),
    crossReferences: toArray(dto.crossReferences).map(mapCrossReference),
    afoCrossReferences: toArray(dto.afoCrossReferences).map(mapCrossReference),
    references: toArray(dto.references)
      .filter((reference) => !reallexikonReferenceIds.has(reference.id))
      .map(createReference),
  }
}

export default class RealiaRepository {
  private readonly apiClient: ApiClient

  constructor(apiClient: ApiClient) {
    this.apiClient = apiClient
  }

  private fetchEntry(path: string, signal?: AbortSignal): Promise<RealiaEntry> {
    return this.apiClient
      .fetchJson<RealiaEntryDto>(path, false, signal)
      .then(mapRealiaEntry)
  }

  find(lemma: string, signal?: AbortSignal): Promise<RealiaEntry> {
    return this.fetchEntry(`/realia/${encodeURIComponent(lemma)}`, signal)
  }

  findByRealiaId(realiaId: string, signal?: AbortSignal): Promise<RealiaEntry> {
    return this.fetchEntry(
      `/realia/by-id/${encodeURIComponent(realiaId)}`,
      signal,
    )
  }

  search(query: string, signal?: AbortSignal): Promise<readonly RealiaEntry[]> {
    const path = `/realia?query=${encodeURIComponent(query)}`
    return this.apiClient
      .fetchJson<RealiaEntryDto[]>(path, false, signal)
      .then((result) => result.map(mapRealiaEntry))
  }

  listAllRealia(): Promise<string[]> {
    return this.apiClient.fetchJson<string[]>(`/realia/all`, false)
  }
}
